import { Checkbox, Listy } from 'antd';
import { CheckboxGroupProps, CheckboxOptionType } from 'antd/es/checkbox';
import { Key, ReactElement, useMemo, useState } from 'react';
import './XmCheckboxList.less';

/** Same shape as AntD `Checkbox.Group` options */
type Option<T> = CheckboxOptionType<T> | string | number;

interface Item<T> {
  key: Key;
  option: CheckboxOptionType<T>;
}

export interface XmCheckboxListProps<T> extends Omit<CheckboxGroupProps<T>, 'options' | 'children'> {
  options?: readonly Option<T>[];
  /** max height of the scrollable area in px, defaults to 200 */
  height?: number;
  /** render only the visible rows, defaults to true */
  virtual?: boolean;
}

const toItem = <T,>(rawOption: Option<T>, index: number): Item<T> => {
  const option: CheckboxOptionType<T> = typeof rawOption === 'object' ? rawOption
    : { label: rawOption, value: rawOption as T };
  const { label, value } = option;
  const key = typeof value === 'string' || typeof value === 'number' ? value
    : typeof label === 'string' || typeof label === 'number' ? label
      : index;
  return { key, option };
};

/**
 * Vertical, scrollable and virtualized list of checkboxes for longer option lists.
 * Behaves like AntD `Checkbox.Group` with `options`, so it can be used as a `Form.Item` child.
 */
export const XmCheckboxList = <T,>({ options, height = 200, virtual = true, className, value, defaultValue, onChange, ...groupProps }: XmCheckboxListProps<T>): ReactElement => {
  const items = useMemo(() => options?.map(toItem), [options]);
  const [innerValue, setInnerValue] = useState<T[]>(defaultValue ?? []);
  const checkedValues = value ?? innerValue;

  const toggle = (toggled: T) => {
    const next = (items ?? []).map(item => item.option.value)
      .filter(optionValue => optionValue === toggled ? !checkedValues.includes(optionValue) : checkedValues.includes(optionValue));
    setInnerValue(next);
    onChange?.(next);
  };

  // skipGroup: Checkbox.Group drops values of unmounted (virtualized) checkboxes on change
  return <Checkbox.Group {...groupProps} className={`xm-checkbox-list ${className || ''}`.trim()}>
    <Listy items={items} height={height} virtual={virtual} rowKey={item => item.key}
      itemRender={({ option: { label, onChange: onOptionChange, ...checkbox } }) => <Checkbox {...checkbox} skipGroup name={groupProps.name}
        checked={checkedValues.includes(checkbox.value)} onChange={e => {
          onOptionChange?.(e);
          toggle(checkbox.value);
        }}>{label}</Checkbox>} />
  </Checkbox.Group>;
};

export default XmCheckboxList;
